import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  ArrowRight, 
  Users, 
  Calendar, 
  CreditCard,
  BarChart3,
  Shield,
  Smartphone,
  Zap,
  TrendingUp
} from 'lucide-react';
import SEOHead from '../seo/SEOHead';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': 'Best Gym Management Software in India 2025',
    'description': 'Discover the best gym management software in India. Compare features, pricing, and benefits.',
    'author': {
      '@type': 'Organization',
      'name': 'GymsSync'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'GymsSync'
    },
    'datePublished': '2025-01-15',
    'dateModified': '2025-01-15'
  };

  return (
    <>
      <SEOHead
        title="Best Gym Management Software in India 2025 | GymsSync"
        description="Discover the best gym management software in India. Compare features, pricing, and benefits. GymsSync offers complete gym ERP solution for fitness studios."
        keywords="best gym management software India, gym software comparison, fitness studio management software, gym ERP India, gymssync, gymsync, best gym software, gym management software India, gym software reviews, gym management system India, top gym software, gym management platform, gym software solution, gym management software comparison, gym software features, gym management software pricing, gym software demo, gym management software free trial"
        ogUrl={typeof window !== 'undefined' ? window.location.href : ''}
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/best-gym-management-software-india` : ''}
        structuredData={structuredData}
      />
      <div className="landing-page">
      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="landing-title"
          >
            Best Gym Management Software in India (2025)
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="landing-subtitle"
          >
            Complete Guide to Choosing the Right Gym ERP Solution for Your Fitness Studio
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <article className="landing-content">
        <div className="content-wrapper">
          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="content-section"
          >
            <h2>Introduction: Why Your Gym Needs Management Software</h2>
            <p>
              Running a gym or fitness studio in India comes with unique challenges. From managing 
              hundreds of members to tracking attendance, processing payments, and maintaining 
              equipment—the list of responsibilities is endless. This is where <strong>gym management 
              software</strong> becomes essential.
            </p>
            <p>
              Modern <strong>gym ERP</strong> solutions like GymsSync automate these processes, saving 
              gym owners 20+ hours per week while improving member satisfaction and increasing revenue. 
              In this comprehensive guide, we'll explore why gym management software is crucial, what 
              features to look for, and how GymsSync stands out as the <strong>best gym software in India</strong>.
            </p>
          </motion.section>

          {/* Why Gyms Need Software */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="content-section"
          >
            <h2>Why Gyms Need Management Software</h2>
            <p>
              Traditional methods of managing a gym—Excel sheets, paper registers, and manual payment 
              tracking—are not only time-consuming but also prone to errors. Here's why modern gyms 
              are switching to <strong>fitness studio management software</strong>:
            </p>
            
            <div className="benefits-list">
              <div className="benefit-item">
                <Zap className="benefit-icon" />
                <div>
                  <h3>Time Savings</h3>
                  <p>Automate repetitive tasks and save 20+ hours weekly on administrative work.</p>
                </div>
              </div>
              <div className="benefit-item">
                <TrendingUp className="benefit-icon" />
                <div>
                  <h3>Revenue Growth</h3>
                  <p>Automated payment reminders and membership renewals can increase revenue by 30%.</p>
                </div>
              </div>
              <div className="benefit-item">
                <Users className="benefit-icon" />
                <div>
                  <h3>Better Member Experience</h3>
                  <p>Self-service portals and seamless check-ins keep members happy and engaged.</p>
                </div>
              </div>
              <div className="benefit-item">
                <BarChart3 className="benefit-icon" />
                <div>
                  <h3>Data-Driven Decisions</h3>
                  <p>Real-time analytics help you make informed decisions about your gym operations.</p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Comparison Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="content-section"
          >
            <h2>GymsSync vs Other Gym Management Software</h2>
            <p>
              While there are several <strong>gym management software</strong> options available in India, 
              GymsSync offers unique advantages that make it the preferred choice for modern fitness studios:
            </p>

            <div className="comparison-grid">
              <div className="comparison-card">
                <h3>GymsSync</h3>
                <ul>
                  <li><CheckCircle2 size={16} /> Indian payment gateway integration</li>
                  <li><CheckCircle2 size={16} /> Affordable pricing starting ₹2,999/month</li>
                  <li><CheckCircle2 size={16} /> Local language support</li>
                  <li><CheckCircle2 size={16} /> 24/7 customer support</li>
                  <li><CheckCircle2 size={16} /> Mobile-responsive design</li>
                  <li><CheckCircle2 size={16} /> Customizable for Indian gym needs</li>
                </ul>
              </div>
              <div className="comparison-card">
                <h3>Other Solutions</h3>
                <ul>
                  <li>❌ Limited Indian payment options</li>
                  <li>❌ Higher pricing (₹5,000+ per month)</li>
                  <li>❌ English-only interface</li>
                  <li>❌ Limited support hours</li>
                  <li>❌ Desktop-focused design</li>
                  <li>❌ Generic features not tailored for India</li>
                </ul>
              </div>
            </div>
          </motion.section>

          {/* Key Features */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="content-section"
          >
            <h2>Essential Features of Best Gym Management Software</h2>
            <p>
              When choosing a <strong>gym membership software</strong>, look for these essential features:
            </p>

            <div className="features-grid">
              <div className="feature-item">
                <Users size={32} />
                <h3>Member Management</h3>
                <p>Complete member profiles, membership tracking, and automated renewal reminders.</p>
              </div>
              <div className="feature-item">
                <Calendar size={32} />
                <h3>Attendance System for Gym</h3>
                <p>Manual check-in system with real-time attendance tracking. QR code and biometric support coming soon.</p>
              </div>
              <div className="feature-item">
                <CreditCard size={32} />
                <h3>Payment Processing</h3>
                <p>Online payments, automated invoicing, and payment reminder systems.</p>
              </div>
              <div className="feature-item">
                <BarChart3 size={32} />
                <h3>Analytics & Reports</h3>
                <p>Revenue reports, member growth analytics, and operational insights.</p>
              </div>
              <div className="feature-item">
                <Shield size={32} />
                <h3>Security & Compliance</h3>
                <p>Bank-level encryption, data backups, and GDPR compliance.</p>
              </div>
              <div className="feature-item">
                <Smartphone size={32} />
                <h3>Mobile Access</h3>
                <p>Mobile-responsive interface for gym owners and members.</p>
              </div>
            </div>
          </motion.section>

          {/* Benefits Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="content-section"
          >
            <h2>Benefits of Using GymsSync</h2>
            <p>
              GymsSync, the <strong>best gym software in India</strong>, offers numerous benefits:
            </p>
            <ul className="benefits-ul">
              <li><strong>Increased Efficiency:</strong> Automate 80% of administrative tasks</li>
              <li><strong>Revenue Growth:</strong> Automated payment collection increases revenue by 30%</li>
              <li><strong>Member Retention:</strong> Better communication and service improve retention rates</li>
              <li><strong>Data Security:</strong> Enterprise-grade security protects member information</li>
              <li><strong>Scalability:</strong> Grow from 10 to 10,000+ members seamlessly</li>
              <li><strong>Cost Savings:</strong> Reduce administrative staff costs by 40%</li>
            </ul>
          </motion.section>

          {/* Screenshots Placeholder */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="content-section"
          >
            <h2>GymsSync Dashboard Preview</h2>
            <div className="screenshot-container">
              <div className="screenshot-placeholder-large">
                <BarChart3 size={64} />
                <p>Interactive Dashboard Screenshot</p>
                <p className="screenshot-note">Real-time analytics, member overview, and revenue tracking</p>
              </div>
            </div>
          </motion.section>

          {/* FAQs */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="content-section"
          >
            <h2>Frequently Asked Questions</h2>
            
            <div className="faq-container">
              <div className="faq-item">
                <h3>What is the best gym management software in India?</h3>
                <p>
                  GymsSync is considered one of the best gym management software solutions in India due to 
                  its affordable pricing, Indian payment gateway support, local language options, and 
                  features specifically designed for Indian gym owners.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>How much does gym management software cost?</h3>
                <p>
                  GymsSync offers affordable yearly pricing starting from ₹4,999/year (≈ ₹417/month) for small gyms, making it one of the most 
                  affordable gym ERP solutions in India. Professional and Enterprise plans are coming soon.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Can gym software integrate with payment gateways?</h3>
                <p>
                  Yes, GymsSync integrates with popular Indian payment gateways like Razorpay, PayU, and 
                  PhonePe, allowing seamless online payment collection.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Is gym management software suitable for small gyms?</h3>
                <p>
                  Absolutely! GymsSync's Starter plan is perfect for small gyms with up to 100 members. 
                  The software scales as your gym grows.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>How does attendance tracking work in gym software?</h3>
                <p>
                  GymsSync currently offers manual check-in system with real-time tracking. QR code scanning and biometric integration are coming soon. All attendance data is recorded in real-time through the dashboard.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Can I manage multiple gym locations with one software?</h3>
                <p>
                  Yes, GymsSync supports multi-location management, allowing you to manage all your gym 
                  branches from a single dashboard with centralized reporting.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>What makes GymsSync different from other gym software?</h3>
                <p>
                  GymsSync is specifically designed for the Indian market with local payment gateways, 
                  affordable pricing, Hindi language support, and features tailored to Indian gym 
                  operations.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Is data migration from existing systems possible?</h3>
                <p>
                  Yes, GymsSync provides free data migration assistance to help you transfer member data, 
                  payment history, and other information from your existing system.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Conclusion & CTA */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="content-section cta-section"
          >
            <h2>Conclusion: Choose GymsSync for Your Gym</h2>
            <p>
              Choosing the right <strong>gym management software</strong> is crucial for the success of 
              your fitness business. GymsSync offers the perfect combination of powerful features, 
              affordable pricing, and Indian market-specific solutions that make it the 
              <strong> best gym software in India</strong>.
            </p>
            <p>
              Whether you run a small fitness studio or a large gym chain, GymsSync's scalable 
              <strong> gym ERP</strong> solution can help you streamline operations, increase revenue, 
              and provide exceptional member experiences.
            </p>
            
            <div className="cta-buttons">
              <Link to="/login" className="cta-button-primary">
                Start Free Trial
                <ArrowRight size={20} />
              </Link>
              <Link to="/" className="cta-button-secondary">
                Learn More
              </Link>
            </div>
          </motion.section>
        </div>
      </article>
    </div>
    </>
  );
};

export default LandingPage;

